import React, { useEffect, useState } from 'react'
import type { ContentDetail, ContentStatus, Collection } from './types'
import { STATUS_PALETTE, STATUS_CONFIG } from './types'
import { useAppLanguage } from '../../i18n'

type ContentDetailModalProps = {
  open: boolean
  contentId: number | null
  collections: Collection[]
  onClose: () => void
  onLoadDetail: (id: number) => Promise<ContentDetail | null>
  onDownloadTranscription: (id: number) => Promise<void>
  onForceTranscribe: (id: number) => Promise<boolean>
  onReindex: (id: number) => Promise<boolean>
  onLLMSegment: (id: number) => Promise<boolean>
  onUpdateCollection: (id: number, collectionId: number | null) => Promise<boolean>
  isWorking: boolean
}

/**
 * Modal de détails d'un contenu
 * Affiche transcription, embeddings, métadonnées
 * Permet de changer la collection
 */
export default function ContentDetailModal({
  open,
  contentId,
  collections,
  onClose,
  onLoadDetail,
  onDownloadTranscription,
  onForceTranscribe,
  onReindex,
  onLLMSegment,
  onUpdateCollection,
  isWorking,
}: ContentDetailModalProps) {
  const { t, language } = useAppLanguage()
  const [detail, setDetail] = useState<ContentDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null)
  const [collectionChanged, setCollectionChanged] = useState(false)

  const locale = language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'fr-FR'

  // Charger les détails à l'ouverture
  useEffect(() => {
    if (!open || !contentId) {
      setDetail(null)
      setError(null)
      return
    }

    const load = async () => {
      setLoading(true)
      setError(null)
      setCollectionChanged(false)
      const data = await onLoadDetail(contentId)
      if (data) {
        setDetail(data)
        setSelectedCollectionId(data.subject_id ?? null)
      } else {
        setError(t.knowledges.contentDetail.cannotLoadDetails)
      }
      setLoading(false)
    }

    load()
  }, [open, contentId, onLoadDetail])

  // Fermer avec Escape
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) return null

  const statusConfig = detail ? STATUS_CONFIG[detail.status] : null
  const statusClass = detail ? STATUS_PALETTE[detail.status] : ''
  const canForceTranscribe = detail && (detail.status === 'uploaded' || detail.status === 'error')

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="absolute inset-x-0 top-10 mx-auto max-w-4xl overflow-hidden rounded-3xl border border-emerald-100/70 bg-white/95 shadow-2xl shadow-emerald-500/20 dark:border-emerald-900/60 dark:bg-zinc-950/95">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-emerald-100/70 bg-emerald-50/40 px-6 py-4 dark:border-emerald-900/60 dark:bg-emerald-900/20">
          <div className="min-w-0 flex-1">
            {loading ? (
              <div className="animate-pulse">
                <div className="h-6 w-48 rounded bg-emerald-200/60 dark:bg-emerald-800/40" />
                <div className="mt-2 h-4 w-24 rounded bg-emerald-100/60 dark:bg-emerald-900/40" />
              </div>
            ) : detail ? (
              <>
                <h2 className="text-lg font-semibold text-zinc-900 dark:text-white truncate">
                  {detail.title}
                </h2>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">ID {detail.id}</span>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass}`}>
                    {statusConfig?.label}
                  </span>
                </div>
              </>
            ) : (
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">{t.knowledges.contentDetail.titleFallback}</h2>
            )}
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-full border border-zinc-200/70 px-3 py-1 text-sm text-zinc-600 transition hover:bg-zinc-100/70 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800/60"
          >
            {t.common.close}
          </button>
        </div>

        {/* Contenu */}
        <div className="max-h-[70vh] overflow-y-auto px-6 py-6 space-y-6">
          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-16 rounded-2xl bg-emerald-100/60 dark:bg-emerald-900/40" />
                ))}
              </div>
              <div className="h-32 rounded-2xl bg-emerald-100/50 dark:bg-emerald-900/30" />
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-200/70 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
              {error}
            </div>
          ) : detail ? (
            <>
              {/* Collection */}
              <div className="rounded-2xl border border-blue-100/60 bg-blue-50/40 p-4 dark:border-blue-900/50 dark:bg-blue-900/20">
                <h3 className="text-sm font-semibold text-zinc-600 dark:text-zinc-300 mb-3">
                  {t.knowledges.contentDetail.collection}
                </h3>
                <div className="flex items-center gap-3">
                  <select
                    value={selectedCollectionId ?? ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? null : parseInt(e.target.value)
                      setSelectedCollectionId(val)
                      setCollectionChanged(val !== (detail.subject_id ?? null))
                    }}
                    className="flex-1 rounded-xl border border-blue-200/70 bg-white/80 px-3 py-2 text-sm text-zinc-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-blue-800 dark:bg-zinc-900/80 dark:text-zinc-200"
                  >
                    <option value="">{t.knowledges.contentDetail.withoutCollection}</option>
                    {collections.map((col) => (
                      <option key={col.id} value={col.id}>
                        {col.title}
                      </option>
                    ))}
                  </select>
                  {collectionChanged && (
                    <button
                      onClick={async () => {
                        if (!contentId) return
                        const success = await onUpdateCollection(contentId, selectedCollectionId)
                        if (success) {
                          setCollectionChanged(false)
                          // Refresh detail
                          const data = await onLoadDetail(contentId)
                          if (data) setDetail(data)
                        }
                      }}
                      disabled={isWorking}
                      className="rounded-full bg-blue-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition hover:bg-blue-600 disabled:opacity-50"
                    >
                      {t.knowledges.contentDetail.move}
                    </button>
                  )}
                </div>
                {detail.subject_label && !collectionChanged && (
                  <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                    {t.knowledges.contentDetail.currentlyInPrefix}{' '}<span className="font-medium">{detail.subject_label}</span>
                  </p>
                )}
              </div>

              {/* Métadonnées */}
              <div className="grid gap-4 sm:grid-cols-2">
                <InfoChip label={t.knowledges.contentDetail.categories} value={detail.categories.join(', ') || '—'} />
                <InfoChip label={t.knowledges.contentDetail.course} value={detail.course_title ?? detail.course_id ?? '—'} />
                <InfoChip label={t.knowledges.contentDetail.chunks} value={String(detail.chunks)} />
                <InfoChip label={t.knowledges.contentDetail.embeddings} value={String(detail.embeddings_total)} />
                <InfoChip label={t.knowledges.contentDetail.file} value={detail.filename || '—'} />
                <InfoChip
                  label={t.knowledges.contentDetail.size}
                  value={detail.size_bytes ? `${(detail.size_bytes / (1024 * 1024)).toFixed(2)} ${t.knowledges.contentDetail.mb}` : '—'}
                />
              </div>

              {/* Embeddings par catégorie */}
              {Object.keys(detail.embeddings_by_category || {}).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-zinc-600 dark:text-zinc-300 mb-3">
                    {t.knowledges.contentDetail.embeddingsByCategory}
                  </h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {Object.entries(detail.embeddings_by_category).map(([category, count]) => (
                      <div
                        key={category}
                        className="flex items-center justify-between rounded-2xl border border-emerald-100/60 bg-white/80 px-3 py-2 text-sm text-zinc-700 dark:border-emerald-900/50 dark:bg-zinc-900/80 dark:text-zinc-200"
                      >
                        <span>{category}</span>
                        <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200">
                          {count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transcription */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-zinc-600 dark:text-zinc-300">
                    {t.knowledges.contentDetail.transcription} ({detail.transcription_length.toLocaleString(locale)} {t.knowledges.contentDetail.characters})
                  </h3>
                  {detail.transcription_language && (
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">
                      {t.knowledges.contentDetail.language} : {detail.transcription_language.toUpperCase()}
                    </span>
                  )}
                </div>
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-2xl border border-zinc-200/70 bg-zinc-50/80 p-4 text-xs text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900/80 dark:text-zinc-200">
                  {detail.transcription_preview || t.knowledges.contentDetail.noTranscription}
                </pre>
              </div>
            </>
          ) : null}
        </div>

        {/* Footer actions */}
        {detail && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-emerald-100/60 bg-emerald-50/40 px-6 py-4 dark:border-emerald-900/50 dark:bg-emerald-900/20">
            <div className="flex flex-wrap gap-2">
              {/* Télécharger transcription */}
              <button
                onClick={() => onDownloadTranscription(detail.id)}
                disabled={isWorking || !detail.transcription_length}
                className="rounded-full border border-emerald-200/70 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50/70 disabled:opacity-50 dark:border-emerald-800 dark:text-emerald-200 dark:hover:bg-emerald-900/40"
                title={!detail.transcription_length ? t.knowledges.contentDetail.transcriptionUnavailable : t.knowledges.contentDetail.downloadTranscription}
              >
                {t.knowledges.contentDetail.downloadTxt}
              </button>

              {/* Ré-indexer */}
              <button
                onClick={() => onReindex(detail.id)}
                disabled={isWorking}
                className="rounded-full border border-zinc-200/70 px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100/70 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800/60"
              >
                {t.knowledges.contentDetail.reindex}
              </button>

              {/* Segmentation IA */}
              <button
                onClick={() => onLLMSegment(detail.id)}
                disabled={isWorking}
                className="rounded-full border border-amber-300/70 bg-amber-50/60 px-4 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-100/80 disabled:opacity-50 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-200 dark:hover:bg-amber-900/40"
              >
                {t.knowledges.contentDetail.llmSegmentation}
              </button>

              {/* Forcer transcription */}
              {canForceTranscribe && (
                <button
                  onClick={() => onForceTranscribe(detail.id)}
                  disabled={isWorking}
                  className="rounded-full border border-blue-200/70 px-4 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50/70 disabled:opacity-50 dark:border-blue-800 dark:text-blue-200 dark:hover:bg-blue-900/40"
                >
                  {t.knowledges.contentDetail.forceTranscribe}
                </button>
              )}
            </div>

            <button
              onClick={onClose}
              className="rounded-full border border-zinc-200/70 px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100/70 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800/60"
            >
              {t.common.close}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

type InfoChipProps = { label: string; value: string }

function InfoChip({ label, value }: InfoChipProps) {
  return (
    <div className="rounded-2xl border border-zinc-200/70 bg-white/80 px-3 py-2 text-sm text-zinc-600 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80 dark:text-zinc-300">
      <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">{label}</div>
      <div className="mt-1 text-sm text-zinc-700 dark:text-zinc-100 truncate">{value}</div>
    </div>
  )
}
